import { type DuelStatus, otherSeat, type Seat } from '../duel/protocol'

/** XOX's name for a seat; X opens by default. */
export type Mark = Seat
export type Cell = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
export type Board = readonly (Mark | null)[]
export type Line = readonly [Cell, Cell, Cell]

/** `draw` only exists in computer games (repetition / ply cap); humans never draw. */
export type GameStatus = DuelStatus

export interface GameState {
  readonly board: Board
  /** Cells owned by each player in placement order (oldest first). Never longer than MAX_MARKS. */
  readonly marks: { readonly X: readonly Cell[]; readonly O: readonly Cell[] }
  readonly turn: Mark
  readonly status: GameStatus
  readonly winner: Mark | null
  readonly winLine: Line | null
  readonly moveCount: number
}

export const MAX_MARKS = 3
export const BOARD_SIZE = 9

export const WIN_LINES: readonly Line[] = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8],
  [0, 3, 6],
  [1, 4, 7],
  [2, 5, 8],
  [0, 4, 8],
  [2, 4, 6],
]

export type MoveError = 'GAME_OVER' | 'NOT_YOUR_TURN' | 'INVALID_CELL' | 'CELL_TAKEN'

export type MoveResult =
  | { ok: true; state: GameState; removed: Cell | null }
  | { ok: false; error: MoveError }

export const other = otherSeat

export function isCell(value: unknown): value is Cell {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < BOARD_SIZE
}

export function createGame(firstTurn: Mark = 'X'): GameState {
  return {
    board: Array.from({ length: BOARD_SIZE }, () => null),
    marks: { X: [], O: [] },
    turn: firstTurn,
    status: 'playing',
    winner: null,
    winLine: null,
    moveCount: 0,
  }
}

export function findWinLine(board: Board, mark: Mark): Line | null {
  for (const line of WIN_LINES) {
    if (board[line[0]] === mark && board[line[1]] === mark && board[line[2]] === mark) return line
  }
  return null
}

/** The cell that will vanish when `mark` makes their next move, or null if they have room. */
export function nextVanishingCell(state: GameState, mark: Mark): Cell | null {
  if (state.status !== 'playing') return null
  const own = state.marks[mark]
  return own.length >= MAX_MARKS ? (own[0] ?? null) : null
}

export function applyMove(state: GameState, mark: Mark, cell: number): MoveResult {
  if (state.status !== 'playing') return { ok: false, error: 'GAME_OVER' }
  if (mark !== state.turn) return { ok: false, error: 'NOT_YOUR_TURN' }
  if (!isCell(cell)) return { ok: false, error: 'INVALID_CELL' }
  // Emptiness is judged on the pre-removal board: a player's own oldest mark still occupies
  // its cell at move time, so it can only be re-targeted on a later turn.
  if (state.board[cell] !== null) return { ok: false, error: 'CELL_TAKEN' }

  const board = [...state.board]
  let own = [...state.marks[mark]]
  let removed: Cell | null = null

  if (own.length >= MAX_MARKS) {
    removed = own[0]!
    board[removed] = null
    own = own.slice(1)
  }

  board[cell] = mark
  own.push(cell)

  const winLine = findWinLine(board, mark)
  const next: GameState = {
    board,
    marks: { ...state.marks, [mark]: own },
    turn: other(mark),
    status: winLine ? 'won' : 'playing',
    winner: winLine ? mark : null,
    winLine,
    moveCount: state.moveCount + 1,
  }
  return { ok: true, state: next, removed }
}

/** Ends the game with `loser` forfeiting (timeout or abandonment). */
export function forfeit(state: GameState, loser: Mark): GameState {
  if (state.status !== 'playing') return state
  return { ...state, status: 'forfeit', winner: other(loser), winLine: null }
}

/** Ends the game with no winner. Only the computer mode declares draws (see solver.ts). */
export function declareDraw(state: GameState): GameState {
  if (state.status !== 'playing') return state
  return { ...state, status: 'draw', winner: null, winLine: null }
}

export function emptyCells(board: Board): Cell[] {
  const out: Cell[] = []
  for (let i = 0; i < BOARD_SIZE; i++) if (board[i] === null) out.push(i as Cell)
  return out
}
